import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../../environments/environment.prod";
import { andAllWhere, andWhere, buildQuery, CombinedFilter, FilterFn, noop, Normalizers, Ops, orderBy, OrderVariant, orWhere, page, select, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { DocumentClassAttributes, MastersUserAttributes, OrderAttributes, TripAttributes, VisitAttributes } from "@albatrosdeveloper/ave-models-package";
import { environment } from "environments/environment.qas";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "app/services/abstract.service";
import { IAPIListResult, IPagination, IPaginationDate, IPaginationResult, IPaginationResultFrequencies, IRequestResult } from "app/common/interfaces";
import { TripAttributesExt } from "./dto/visit-report.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { GroupData } from "app/shared/components/map-modal/models/map-modal.interface";
import { TripAttributesGps } from "../visit-gps/dto/visit-gps.interface";


const apiUrl = env.API_AVE_TRIP_URL;
const moduleName = 'visit';
const moduleCheck = 'visit-check';
const moduleVisitName = 'visit';
const moduleTripName = 'trip';

@Injectable({
  providedIn: "root",
})
export class TripService extends AbstractService<
  TripAttributes,
  Partial<TripAttributes>,
  Partial<TripAttributes>
> {

  currentFilter: CombinedFilter<VisitAttributes>;
  currentFilterData: any;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<TripAttributesExt>, dataToFilter?
  ): Observable<IPaginationResult<TripAttributesExt>> {

    let dataToSend = {
      search: options.search
    }

    const arrayQueries: FilterFn<TripAttributesExt>[] = [];

    const query = buildQuery<TripAttributesExt>(
      ...arrayQueries,

      orderBy(
        options.sort ?? "dateHourEndTeoric",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<TripAttributesExt>>(API_URL);
  }

  findAllVisitWithCount(
    options: IPagination<VisitAttributes>, dataToFilter?
  ): Observable<IPaginationResult<VisitAttributes>> {

    let dataToSend = {
      search: options.search
    }

    const arrayQueries: FilterFn<VisitAttributes>[] = [];

    arrayQueries.push(where(<any>'trip.id', Ops.eq(dataToFilter.tripId)))

    const query = buildQuery<VisitAttributes>(
      ...arrayQueries,

      orderBy(
        options.sort ?? "dateTimeEnd",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleVisitName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<VisitAttributes>>(API_URL);
  }

  //filter trip
  findToComboBox(
    subRoute: number,
    date: Date,
    type: boolean
  ): Observable<IRequestResult<TripAttributesExt[]>> {
    let arrayQueries = []
    if (date) {
      const copy1 = new Date(date.getTime())
      const copy2 = new Date(date.getTime());
      const newDate = new Date(date.setSeconds(date.getSeconds() + 1));

      const dateBefore = new Date(copy1.setDate(copy1.getDate() - 15));
      const dateAfter = new Date(copy2.setDate(copy2.getDate() + 15));
    }

    // Determinar los estados según el tipo
    const statusTripIds = type
      ? [2, 3, 4]  // Valores cuando type es de entrega
      : [2, 3];    // Valores cuando type es de asistente de carga

    const query = buildQuery<TripAttributesExt>(
      where("_deleted", Ops.eq(false)),
      andAllWhere("statusId", StatusEnum.ACTIVE),
      andAllWhere('subRoute.id' as keyof TripAttributesExt, Ops.eq(subRoute)),
      andAllWhere('statusTripId', Ops.in(...statusTripIds)),
      ...arrayQueries,
      select(["id", "externalKeys", "dateHourStartTeoric", "dateHourEndTeoric", "loadDateTime", "subRoute", "statusTripId"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleTripName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<TripAttributesExt[]>>(API_URL);
  }

   /**
   * * 📍 Used in:
   * * - Reportes/Visitas/Efectividad (`visit-report.component.ts`)
   */
  findByFilter(filter: CombinedFilter<VisitAttributes>, filterData: any, options?: RequestOptions) {
    const API_URL: string = apiUrl + `api/${moduleName}/withCountByTrip`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.post<IPaginationResultFrequencies<VisitAttributes>>(API_URL,
      { filter: filter },
      { headers });
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   * 
   * * 📍 Used in:
   * * - Reportes/Visitas/Efectividad (`visit-report.component.ts`)
   */
  findAllWithCountByTrip(
    options: IPaginationDate<VisitAttributes>,
    dataToFilter: IFilters,
  ): Observable<IPaginationResult<VisitAttributes>> {

    const query = buildQuery<VisitAttributes>(
      where("trip.subRoute.name" as keyof VisitAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("user.user_login.username" as keyof VisitAttributes, Ops.like(options.search ?? "", "i")),
      dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0 ? andAllWhere('subRoute.id' as keyof VisitAttributes, Ops.in(...dataToFilter.subRouteId)) : noop(),
      dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0 ? andAllWhere('salesOffice.id' as keyof VisitAttributes, Ops.in(...dataToFilter.salesOfficeId)) : noop(),
      dataToFilter.customerId && dataToFilter.customerId.length > 0 ? andAllWhere('shipTo.id' as keyof VisitAttributes, Ops.in(...dataToFilter.customerId)) : noop(),
      dataToFilter.companyId ? andAllWhere('company.id' as keyof VisitAttributes, Ops.eq(dataToFilter.companyId)) : noop(),
      dataToFilter.startDate && dataToFilter.endDate
        ? andAllWhere("dateTimeStart", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
        : noop(),
      orderBy(
        options.sort ?? "trip.trip.dateHourStart" as keyof VisitAttributes,
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;
    //this.currentFilterData = dataToFilter;

    const API_URL: string = apiUrl + `api/${moduleName}/withCountByTrip`;
    return this.http.post<IPaginationResult<VisitAttributes>>(API_URL, {
      filter: query
    });
  }

  exportVisitXLSX(
    options: IPaginationDate<VisitAttributes>,
  ): Observable<IPaginationResult<VisitAttributes>> {

    const query = buildQuery<VisitAttributes>(
      where("trip.subRoute.name" as keyof VisitAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("user.user_login.username" as keyof VisitAttributes, Ops.like(options.search ?? "", "i")),

      // Add date range filter if startDate and endDate are provided in the options
      options.startDate && options.endDate
        ? andWhere("trip.dateHourStart" as keyof VisitAttributes, Ops.btwnDates(options.startDate, options.endDate))
        : noop(),

      //   companyIds && companyIds.length > 0 ? andAllWhere('company.id' as keyof VisitAttributes, Ops.in(...companyIds)) : noop(),
      orderBy(
        options.sort ?? "trip.trip.dateHourStart" as keyof VisitAttributes,
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/report?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<VisitAttributes>>(API_URL);
  }

  /**
* Pagination for trip in monitor
* @param options 
* @returns Paginated data
*/
  findAllWithCountByTripMonitor(
    options: IPagination<VisitAttributes>,
    tripId: number,
  ): Observable<IPaginationResult<VisitAttributes>> {
    const query = buildQuery<VisitAttributes>(
      // where('_deleted', Ops.eq(false)),
      where('trip.id' as keyof VisitAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "dateTimeStart",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<VisitAttributes>>(API_URL);
  }

  /**
  * Pagination for trip in monitor
  * @param options 
  * @returns Paginated data
  */
  findAllTripGroupShipTo(
    options: IPagination<VisitAttributes>,
    tripId: number,
    filter: {
      groupIds: string[]
      shipTo: string[],
    }
  ): Observable<IAPIListResult<GroupData>> {
    const query = buildQuery<VisitAttributes>(
      where('trip.id' as keyof VisitAttributes, Ops.eq(tripId)),
      filter.shipTo && filter.shipTo.length > 0 ? 
        andAllWhere('shipTo.id' as keyof VisitAttributes, Ops.in(...filter.shipTo)) :
        noop(),
      orderBy(
        options.sort ?? "dateTimeStart",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/group/ship_to`;
    return this.http.post<IAPIListResult<GroupData>>(API_URL, {
      filter: query,
      groupIds: filter.groupIds && filter.groupIds.length > 0 ? filter.groupIds : []
    });
  }

  /**
 * Pagination para check In/Check Out de visitas
 * @param options Pagination options
 * @returns Paginated data
  * * 📍 Used in:
  * * - Reportes/Visitas/Check In/Out (`visit-check.component.ts`)
  */
  findAllWithCountCheckIn(
    options: IPaginationDate<VisitAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<VisitAttributes>> {

    const query = buildQuery<VisitAttributes>(
      where("subRoute.name" as keyof VisitAttributes, Ops.like(options.search ?? "", "i")),
      // andAllWhere("dateTimeStart", Ops.btwnDates(date.start, date.end)),
      dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0 ? andAllWhere('subRoute.id' as keyof VisitAttributes, Ops.in(...dataToFilter.subRouteId)) : noop(),
      dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0 ? andAllWhere('salesOffice.id' as keyof VisitAttributes, Ops.in(...dataToFilter.salesOfficeId)) : noop(),
      dataToFilter.customerId && dataToFilter.customerId.length > 0 ? andAllWhere('shipTo.id' as keyof VisitAttributes, Ops.in(...dataToFilter.customerId)) : noop(),
      dataToFilter.companyId ? andAllWhere('company.id' as keyof VisitAttributes, Ops.eq(dataToFilter.companyId)) : noop(),
      dataToFilter.startDate && dataToFilter.endDate
        ? andAllWhere("dateTimeStart", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
        : noop(),
      orderBy(
        options.sort ?? "dateTimeStart" as keyof VisitAttributes,
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiUrl + `api/${moduleCheck}/withCount`;
    return this.http.post<IPaginationResult<VisitAttributes>>(API_URL, {
      filter: query
    });
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   *  * * 📍 Used in:
   * * - Reportes/Visitas/Check In/Out (`visit-gps.component.ts`)
   */
  findAllWithCountLocations(
    options: IPagination<TripAttributesGps>, dataToFilter?: IFilters
  ): Observable<IPaginationResult<TripAttributesGps>> {
    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<TripAttributesGps>[] => {
      const deletedFilter: FilterFn<TripAttributesGps> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter.startDate && dataToFilter.endDate
          ? andWhere("dateHourStart", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
          : noop(),
        dataToFilter?.subRouteId && dataToFilter?.subRouteId.length > 0
          ? andWhere("subRoute.id" as keyof TripAttributesGps, Ops.in(...dataToFilter.subRouteId))
          : noop(),
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por id (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<TripAttributesGps>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<TripAttributesGps>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("subRoute.name" as keyof TripAttributesGps, Ops.like(options.search!, "i"))
    ] : [];

     const searchByUserQueryObject: FilterFn<TripAttributesGps>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("user.user_login.username" as keyof TripAttributesGps, Ops.like(options.search!, "i"))
    ] : [];

    const query = buildQuery<TripAttributesGps>(
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchByUserQueryObject,

      orderBy(
        options.sort ?? "dateHourEndTeoric",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleTripName}/withCountAndLocations?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<TripAttributesGps>>(API_URL);
  }
}
